import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ArrowRight, Truck } from "lucide-react";
import { toast } from "sonner";
import SiteHeader from "@/components/shop/SiteHeader";
import SiteFooter from "@/components/shop/SiteFooter";
import CartDrawer from "@/components/shop/CartDrawer";
import { useCart } from "@/context/CartContext";
import { api, fmtUSD } from "@/lib/api";

export default function Cart() {
  const navigate = useNavigate();
  const { items, subtotal, shipping, total, updateQty, removeItem } = useCart();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/create-session", {
        items: items.map((i) => ({ slug: i.slug, quantity: i.quantity })),
        origin_url: window.location.origin,
        customer_email: email || undefined,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (e) {
      console.error(e);
      toast.error("We couldn't start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/50 text-slate-900" data-testid="cart-page">
      <SiteHeader />
      <CartDrawer />

      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-10 md:py-14">
        <p className="text-xs uppercase tracking-[0.25em] text-amber-500 font-bold">Your bag</p>
        <h1 className="mt-2 font-display text-4xl md:text-5xl font-black text-[#0f2a5c]">Review & Checkout</h1>

        {items.length === 0 ? (
          <div className="mt-14 text-center py-20 rounded-3xl bg-white border border-slate-100" data-testid="cart-page-empty">
            <p className="font-display text-2xl font-bold text-slate-900">Nothing in the bag yet</p>
            <p className="text-slate-500 mt-2">Let&apos;s find you something soft.</p>
            <Link to="/shop" className="inline-block mt-6 px-6 py-3 rounded-full bg-[#173a7a] text-white font-semibold" data-testid="cart-page-shop-btn">
              Shop Products
            </Link>
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Line items */}
            <div className="lg:col-span-2 space-y-4" data-testid="cart-page-items">
              {items.map((item) => (
                <div key={item.slug} className="flex gap-4 p-5 bg-white rounded-2xl border border-slate-100" data-testid={`cart-page-item-${item.slug}`}>
                  <Link to={`/shop/${item.slug}`}>
                    <img src={item.image} alt={item.name} className="w-24 h-24 rounded-xl bg-sky-50 object-contain p-2" />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/shop/${item.slug}`}>
                      <p className="font-display font-bold text-lg text-slate-900 hover:text-[#173a7a]">{item.name}</p>
                    </Link>
                    <p className="text-sm text-slate-500 mt-1">{fmtUSD(item.price)} each</p>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center rounded-full border border-slate-200">
                        <button
                          data-testid={`cart-page-qty-decrease-${item.slug}`}
                          onClick={() => updateQty(item.slug, item.quantity - 1)}
                          className="p-2 hover:text-[#173a7a]"
                        >
                          <Minus size={14} />
                        </button>
                        <span className="w-10 text-center font-semibold" data-testid={`cart-page-qty-${item.slug}`}>{item.quantity}</span>
                        <button
                          data-testid={`cart-page-qty-increase-${item.slug}`}
                          onClick={() => updateQty(item.slug, item.quantity + 1)}
                          className="p-2 hover:text-[#173a7a]"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                      <p className="font-bold text-slate-900">{fmtUSD(item.price * item.quantity)}</p>
                      <button
                        data-testid={`cart-page-remove-${item.slug}`}
                        onClick={() => removeItem(item.slug)}
                        className="text-slate-400 hover:text-red-500 p-2"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {subtotal < 25 && (
                <div className="flex items-center gap-2 p-4 rounded-2xl bg-amber-50 text-amber-900 text-sm" data-testid="cart-page-shipping-hint">
                  <Truck size={16} />
                  Add {fmtUSD(25 - subtotal)} more for free shipping.
                </div>
              )}
            </div>

            {/* Summary */}
            <aside className="rounded-2xl bg-white border border-slate-100 p-6 h-fit sticky top-24" data-testid="cart-page-summary">
              <p className="font-display font-extrabold text-xl text-[#0f2a5c]">Order Summary</p>
              <div className="mt-5 space-y-3 text-sm">
                <div className="flex justify-between text-slate-600">
                  <span>Subtotal</span>
                  <span data-testid="cart-page-subtotal">{fmtUSD(subtotal)}</span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>Shipping</span>
                  <span data-testid="cart-page-shipping">{shipping === 0 ? "Free" : fmtUSD(shipping)}</span>
                </div>
                <div className="pt-3 border-t border-slate-100 flex justify-between font-display font-bold text-xl text-[#173a7a]">
                  <span>Total</span>
                  <span data-testid="cart-page-total">{fmtUSD(total)}</span>
                </div>
              </div>
              <div className="mt-6">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500 block mb-2">
                  Email (for receipt)
                </label>
                <input
                  data-testid="cart-page-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[#173a7a]"
                />
              </div>
              <button
                data-testid="cart-page-checkout-btn"
                disabled={loading}
                onClick={checkout}
                className="mt-4 w-full flex items-center justify-center gap-2 py-4 rounded-full bg-[#173a7a] text-white font-semibold hover:bg-[#1a4694] disabled:opacity-60"
              >
                {loading ? "Redirecting to Stripe…" : <>Secure Checkout <ArrowRight size={16} /></>}
              </button>
              <p className="mt-4 text-[11px] text-center text-slate-400">
                Guest checkout · Powered by Stripe · Test card 4242 4242 4242 4242
              </p>
            </aside>
          </div>
        )}
      </div>

      <SiteFooter />
    </div>
  );
}
